import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface TokenPoolItem {
  id: number;
  name: string;
  is_active: boolean;
  hourly_limit: number;
  current_usage: number;
  remaining_quota: number;
  total_usage: number;
  created_at: string;
  updated_at: string;
  last_reset_time: string;
}

interface TokenPoolStats {
  total_tokens: number;
  active_tokens: number;
  total_quota: number;
  used_quota: number;
  remaining_quota: number;
  usage_percentage: number;
}

const TokenPoolManagement: React.FC = () => {
  const [tokens, setTokens] = useState<TokenPoolItem[]>([]);
  const [stats, setStats] = useState<TokenPoolStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingToken, setEditingToken] = useState<TokenPoolItem | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTokenData, setNewTokenData] = useState({
    name: '',
    token: '',
    hourly_limit: 5000
  });

  useEffect(() => {
    loadTokens();
    loadStats();
  }, []);

  const loadTokens = async () => {
    try {
      const response = await apiService.getTokenPool();
      if (response.success) {
        setTokens(response.tokens || []);
      }
    } catch (error) {
      console.error('加载Token池失败:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.getTokenPoolStats();
      if (response.success) {
        setStats(response.stats || {});
      }
    } catch (error) {
      console.error('加载统计信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToken = async () => {
    try {
      const response = await apiService.addToken(newTokenData);
      if (response.success) {
        alert('Token添加成功！');
        setShowAddForm(false);
        setNewTokenData({ name: '', token: '', hourly_limit: 5000 });
        loadTokens();
        loadStats();
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Token添加失败');
    }
  };

  const handleUpdateToken = async (token: TokenPoolItem) => {
    try {
      const response = await apiService.updateToken(token.id, {
        name: token.name,
        is_active: token.is_active,
        hourly_limit: token.hourly_limit
      });
      if (response.success) {
        setEditingToken(null);
        loadTokens();
        loadStats();
        alert('Token更新成功！');
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Token更新失败');
    }
  };

  const handleDeleteToken = async (tokenId: number) => {
    if (!confirm('确定要删除这个Token吗？')) return;

    try {
      const response = await apiService.deleteToken(tokenId);
      if (response.success) {
        loadTokens();
        loadStats();
        alert('Token删除成功！');
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Token删除失败');
    }
  };

  const toggleTokenStatus = async (token: TokenPoolItem) => {
    const updatedToken = { ...token, is_active: !token.is_active };
    await handleUpdateToken(updatedToken);
  };

  if (loading) {
    return <div className="loading">加载中...</div>;
  }

  return (
    <div className="token-pool-management">
      <div className="management-header">
        <h2>🔑 Token池管理</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <i className="fas fa-plus"></i> {showAddForm ? '取消添加' : '添加Token'}
        </button>
      </div>

      {/* 统计信息 */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total_tokens}</div>
            <div className="stat-label">总Token数</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.active_tokens}</div>
            <div className="stat-label">活跃Token</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total_quota.toLocaleString()}</div>
            <div className="stat-label">总配额/小时</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.remaining_quota.toLocaleString()}</div>
            <div className="stat-label">剩余配额</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.usage_percentage}%</div>
            <div className="stat-label">使用率</div>
          </div>
        </div>
      )}

      {/* 添加Token表单 */}
      {showAddForm && (
        <div className="add-token-form" style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ marginBottom: '15px', color: '#333' }}>添加新Token</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '15px', alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Token名称</label>
              <input
                type="text"
                value={newTokenData.name}
                onChange={(e) => setNewTokenData({...newTokenData, name: e.target.value})}
                placeholder="例如：备用Token-1"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>GitHub Token</label>
              <input
                type="text"
                value={newTokenData.token}
                onChange={(e) => setNewTokenData({...newTokenData, token: e.target.value})}
                placeholder="github_pat_..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>每小时限制</label>
              <input
                type="number"
                value={newTokenData.hourly_limit}
                onChange={(e) => setNewTokenData({...newTokenData, hourly_limit: parseInt(e.target.value) || 5000})}
                min="1"
                max="5000"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div>
              <button
                onClick={handleAddToken}
                className="btn btn-success"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                添加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token列表 */}
      <div className="tokens-table">
        <table>
          <thead>
            <tr>
              <th>名称</th>
              <th>状态</th>
              <th>每小时限制</th>
              <th>当前使用</th>
              <th>剩余配额</th>
              <th>总使用次数</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(token => (
              <tr key={token.id}>
                <td>
                  {editingToken?.id === token.id ? (
                    <input
                      type="text"
                      value={editingToken.name}
                      onChange={(e) => setEditingToken({...editingToken, name: e.target.value})}
                      className="form-control"
                    />
                  ) : (
                    token.name
                  )}
                </td>
                <td>
                  <span className={`status-badge ${token.is_active ? 'active' : 'inactive'}`}>
                    {token.is_active ? '启用' : '禁用'}
                  </span>
                </td>
                <td>
                  {editingToken?.id === token.id ? (
                    <input
                      type="number"
                      value={editingToken.hourly_limit}
                      onChange={(e) => setEditingToken({...editingToken, hourly_limit: parseInt(e.target.value)})}
                      className="form-control"
                      min="1"
                      max="5000"
                    />
                  ) : (
                    token.hourly_limit.toLocaleString()
                  )}
                </td>
                <td>{token.current_usage.toLocaleString()}</td>
                <td>{token.remaining_quota.toLocaleString()}</td>
                <td>{token.total_usage.toLocaleString()}</td>
                <td>{new Date(token.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    {editingToken?.id === token.id ? (
                      <>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleUpdateToken(editingToken)}
                        >
                          保存
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setEditingToken(null)}
                        >
                          取消
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => setEditingToken(token)}
                        >
                          编辑
                        </button>
                        <button
                          className={`btn btn-sm ${token.is_active ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => toggleTokenStatus(token)}
                        >
                          {token.is_active ? '禁用' : '启用'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteToken(token.id)}
                        >
                          删除
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 添加Token模态框已移除 */}


    </div>
  );
};

export default TokenPoolManagement;
